import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useLanguage } from '../contexts/LanguageContext';
import { FileDown, Loader2, CheckCircle2 } from 'lucide-react';
import API from '../utils/api';

const MONTHS_DE = ['', 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const MONTHS_EN = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const PDFExportModal = ({ open, onOpenChange, accounts, selectedAccountId }) => {
  const { language } = useLanguage();
  const months = language === 'de' ? MONTHS_DE : MONTHS_EN;
  
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  
  const [accountId, setAccountId] = useState(selectedAccountId || '');
  const [startMonth, setStartMonth] = useState(1);
  const [startYear, setStartYear] = useState(currentYear);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [endYear, setEndYear] = useState(currentYear);
  const [includeStats, setIncludeStats] = useState(true);
  const [includeAnalysis, setIncludeAnalysis] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Update accountId when selectedAccountId changes or modal opens
  useEffect(() => {
    if (open && selectedAccountId) {
      setAccountId(selectedAccountId);
    }
  }, [open, selectedAccountId]);

  const handleExport = async () => {
    if (!accountId) return;
    
    setLoading(true);
    setSuccess(false);
    
    try {
      const response = await fetch(`${API}/export/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          account_id: accountId,
          start_month: startMonth,
          start_year: startYear,
          end_month: endMonth,
          end_year: endYear,
          include_stats: includeStats,
          include_analysis: includeAnalysis,
        }),
      });
      
      if (response.ok) {
        // Get filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'TradeJourn_Report.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename=(.+)/);
          if (filenameMatch) {
            filename = filenameMatch[1].replace(/"/g, '');
          }
        }
        
        // Download the PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onOpenChange(false);
        }, 2000);
      }
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setLoading(false);
    }
  };

  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="h-5 w-5 text-primary" />
            {language === 'de' ? 'PDF Export' : 'PDF Export'}
          </DialogTitle>
          <DialogDescription>
            {language === 'de' 
              ? 'Exportiere deine Trading-Daten als professionellen PDF-Report.'
              : 'Export your trading data as a professional PDF report.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Account Selection */}
          <div>
            <Label>{language === 'de' ? 'Konto' : 'Account'}</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="mt-1" data-testid="export-account-select">
                <SelectValue placeholder={language === 'de' ? 'Konto wählen...' : 'Select account...'} />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((acc) => (
                  <SelectItem key={acc.account_id} value={acc.account_id}>
                    {acc.name} ({acc.broker || 'N/A'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{language === 'de' ? 'Von' : 'From'}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Select value={startMonth.toString()} onValueChange={(v) => setStartMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.slice(1).map((m, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={startYear.toString()} onValueChange={(v) => setStartYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>{language === 'de' ? 'Bis' : 'To'}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Select value={endMonth.toString()} onValueChange={(v) => setEndMonth(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.slice(1).map((m, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={endYear.toString()} onValueChange={(v) => setEndYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          {/* Options */}
          <div className="space-y-3 pt-2">
            <Label>{language === 'de' ? 'Inhalt' : 'Content'}</Label>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-stats" 
                checked={includeStats} 
                onCheckedChange={setIncludeStats}
              />
              <label htmlFor="include-stats" className="text-sm cursor-pointer">
                {language === 'de' ? 'Performance-Statistiken einschließen' : 'Include performance statistics'}
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-analysis" 
                checked={includeAnalysis} 
                onCheckedChange={setIncludeAnalysis}
              />
              <label htmlFor="include-analysis" className="text-sm cursor-pointer">
                {language === 'de' ? 'Skill-Scores einschließen (falls vorhanden)' : 'Include skill scores (if available)'}
              </label>
            </div>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {language === 'de' ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={!accountId || loading}
            className="glow-primary"
            data-testid="export-pdf-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {language === 'de' ? 'Exportiert...' : 'Exporting...'}
              </>
            ) : success ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {language === 'de' ? 'Fertig!' : 'Done!'}
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                {language === 'de' ? 'PDF exportieren' : 'Export PDF'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PDFExportModal;
